import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Controller, useForm } from "react-hook-form";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import Loading from "components/Loading";
import ConfirmDialog from "components/common/ConfirmDialog";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import { useAuth } from "hooks/useAuth";
import { db } from "services/firebase";
import { isFirestorePermissionDenied } from "utils/firestoreErrors";

const LocationHierarchyPage = () => {
  const { organizationId, userData, isGlobalAdmin } = useAuth();
  const institutionId =
    userData?.activeInstitutionId ||
    userData?.institutionId ||
    organizationId ||
    null;
  const canManage =
    Boolean(isGlobalAdmin) ||
    (userData?.role === "admin" && Boolean(institutionId));

  const [locations, setLocations] = useState([]);
  const [hierarchyNodes, setHierarchyNodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [clearTarget, setClearTarget] = useState(null);
  const [clearing, setClearing] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { locationId: "", hierarchyNodeId: "" },
  });

  const load = useCallback(async () => {
    if (!institutionId && !isGlobalAdmin) {
      setLocations([]);
      setHierarchyNodes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const queries = [];
      if (institutionId) {
        queries.push(
          db
            .collection("locations")
            .where("institutionId", "==", institutionId)
            .get(),
        );
        queries.push(
          db
            .collection("hierarchyNodes")
            .where("institutionId", "==", institutionId)
            .where("isActive", "==", true)
            .get(),
        );
      }
      const [locSnap, nodesSnap] = await Promise.all(queries);
      setLocations(
        (locSnap?.docs || []).map((doc) => ({ id: doc.id, ...doc.data() })),
      );
      setHierarchyNodes(
        (nodesSnap?.docs || [])
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((n) => n.level >= 4 && n.level <= 6),
      );
    } catch (err) {
      if (!isFirestorePermissionDenied(err)) {
        setError(err.message || "Failed to load locations.");
      }
      setLocations([]);
      setHierarchyNodes([]);
    } finally {
      setLoading(false);
    }
  }, [institutionId, isGlobalAdmin]);

  useEffect(() => {
    load();
  }, [load]);

  const nodeOptions = useMemo(
    () =>
      hierarchyNodes.map((node) => ({
        id: node.id,
        label: `${node.name || node.id} (L${node.level})`,
      })),
    [hierarchyNodes],
  );

  const setMapping = async ({ locationId, hierarchyNodeId }) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const fn = firebase.functions().httpsCallable("setLocationHierarchyNode");
      await fn({
        locationId: locationId.trim(),
        hierarchyNodeId: hierarchyNodeId || null,
        institutionId,
      });
      setSuccess("Location linked to hierarchy node.");
      reset({ locationId: "", hierarchyNodeId: "" });
      await load();
    } catch (err) {
      setError(err.message || "Failed to link location.");
    } finally {
      setSaving(false);
    }
  };

  const clearMapping = async () => {
    if (!clearTarget) return;
    setClearing(true);
    setError(null);
    try {
      const fn = firebase.functions().httpsCallable("setLocationHierarchyNode");
      await fn({
        locationId: clearTarget.id,
        hierarchyNodeId: null,
        institutionId,
      });
      setSuccess("Hierarchy link cleared.");
      setClearTarget(null);
      await load();
    } catch (err) {
      setError(err.message || "Failed to clear link.");
    } finally {
      setClearing(false);
    }
  };

  if (!canManage) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">
          Institution admin access is required to map clinic locations.
        </Alert>
      </Box>
    );
  }

  if (loading) return <Loading page />;

  return (
    <Box>
      <DashboardPageHeader
        title="Clinic location mapping"
        subtitle="Link CareMap locations to ASI hierarchy nodes (levels 4–6) so check-in can grant patient entitlements and route TCIs."
      />

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      ) : null}

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Link a location
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Paste a location document ID from CareMap facility signup, then pick
          the matching hierarchy node.
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit(setMapping)}
          noValidate
        >
          <Stack spacing={2} direction={{ xs: "column", sm: "row" }} alignItems="flex-start">
            <Controller
              name="locationId"
              control={control}
              rules={{ required: "Location ID is required" }}
              render={({ field, fieldState }) => (
                <TextField
                  {...field}
                  label="Location ID"
                  error={Boolean(fieldState.error)}
                  helperText={fieldState.error?.message}
                  fullWidth
                  sx={{ maxWidth: 360 }}
                />
              )}
            />
            <Controller
              name="hierarchyNodeId"
              control={control}
              rules={{ required: "Select a hierarchy node" }}
              render={({ field, fieldState }) => (
                <FormControl fullWidth sx={{ maxWidth: 360 }} error={Boolean(fieldState.error)}>
                  <InputLabel id="hierarchy-node-label">Hierarchy node</InputLabel>
                  <Select
                    {...field}
                    labelId="hierarchy-node-label"
                    label="Hierarchy node"
                  >
                    {nodeOptions.map((opt) => (
                      <MenuItem key={opt.id} value={opt.id}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
            <LoadingButton type="submit" variant="contained" loading={saving}>
              Link
            </LoadingButton>
          </Stack>
        </Box>
      </Paper>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Location</TableCell>
              <TableCell>Location ID</TableCell>
              <TableCell>Hierarchy node</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {locations.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="text.secondary">
                    No linked locations yet. Link one above to populate this list.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              locations.map((loc) => (
                <TableRow key={loc.id}>
                  <TableCell>{loc.title || loc.name || "—"}</TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                      {loc.id}
                    </Typography>
                  </TableCell>
                  <TableCell>{loc.hierarchyNodeId || "—"}</TableCell>
                  <TableCell align="right">
                    <LoadingButton
                      size="small"
                      color="error"
                      onClick={() => setClearTarget(loc)}
                    >
                      Clear
                    </LoadingButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ConfirmDialog
        open={Boolean(clearTarget)}
        title="Clear hierarchy link?"
        message={
          clearTarget
            ? `Remove the hierarchy mapping from ${clearTarget.title || clearTarget.id}? Check-in entitlements will stop until remapped.`
            : ""
        }
        confirmColor="error"
        confirmLabel="Clear link"
        loading={clearing}
        onConfirm={clearMapping}
        onClose={() => setClearTarget(null)}
      />
    </Box>
  );
};

export default LocationHierarchyPage;
